import { Editor, mergeAttributes } from '@tiptap/core';
import OrderedList from '@tiptap/extension-ordered-list';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { convertListType } from './list-type-conversion';

const LIST_NODE_NAMES = new Set(['orderedList', 'bulletList', 'taskList']);

function toPositiveInteger(value: unknown, fallback = 1): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(1, Math.floor(value));
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      return Math.max(1, parsed);
    }
  }

  return fallback;
}

function countDirectListItems(node: ProseMirrorNode): number {
  let count = 0;
  node.forEach((child) => {
    if (child.type.name === 'listItem') {
      count += 1;
    }
  });
  return count;
}

export function isEmptyParagraphDirectlyAfterList(doc: ProseMirrorNode, from: number): boolean {
  const $from = doc.resolve(from);

  if ($from.parent.type.name !== 'paragraph' || $from.parent.content.size !== 0) {
    return false;
  }

  const containerDepth = $from.depth - 1;
  if (containerDepth < 0) {
    return false;
  }

  const indexInContainer = $from.index(containerDepth);
  if (indexInContainer <= 0) {
    return false;
  }

  const container = $from.node(containerDepth);
  const previousSibling = container.child(indexInContainer - 1);
  return LIST_NODE_NAMES.has(previousSibling.type.name);
}

export function getNextOrderedListStartFromDoc(doc: ProseMirrorNode, from: number): number | null {
  let previousStart: number | null = null;
  let previousItemCount = 0;
  let previousPos = -1;

  doc.descendants((node, pos) => {
    if (pos >= from) {
      return false;
    }

    if (node.type.name !== 'orderedList') {
      return true;
    }

    const parent = doc.resolve(pos).parent;
    if (parent.type.name === 'listItem') {
      return true;
    }

    if (pos > previousPos) {
      previousStart = toPositiveInteger(node.attrs['start'], 1);
      previousItemCount = countDirectListItems(node);
      previousPos = pos;
    }

    return true;
  });

  if (previousStart === null) {
    return null;
  }

  return previousStart + previousItemCount;
}

function orderedListAttributes(start: number): Record<string, number> | undefined {
  return start > 1 ? { start } : undefined;
}

function startOrderedList(editor: Editor, start = 1): boolean {
  const attrs = orderedListAttributes(start);

  if (isEmptyParagraphDirectlyAfterList(editor.state.doc, editor.state.selection.from)) {
    return editor.chain().focus().wrapInList('orderedList', attrs).run();
  }

  return editor.chain().focus().toggleList('orderedList', 'listItem', false, attrs).run();
}

export function continueOrderedList(editor: Editor): boolean {
  const from = editor.state.selection.from;
  const nextStart = getNextOrderedListStartFromDoc(editor.state.doc, from);

  if (nextStart && nextStart > 1) {
    return startOrderedList(editor, nextStart);
  }

  return startOrderedList(editor, 1);
}

export function restartOrderedList(editor: Editor): boolean {
  if (editor.isActive('orderedList')) {
    return editor.chain().focus().updateAttributes('orderedList', { start: 1 }).run();
  }

  return startOrderedList(editor, 1);
}

export function toggleBulletListSafely(editor: Editor): boolean {
  if (editor.isActive('bulletList')) {
    return editor.chain().focus().toggleBulletList().run();
  }

  if (editor.isActive('orderedList') || editor.isActive('taskList')) {
    return convertListType(editor, 'bulletList');
  }

  if (isEmptyParagraphDirectlyAfterList(editor.state.doc, editor.state.selection.from)) {
    return editor.chain().focus().wrapInList('bulletList').run();
  }

  return editor.chain().focus().toggleBulletList().run();
}

export function toggleOrderedListSafely(editor: Editor): boolean {
  if (editor.isActive('orderedList')) {
    return editor.chain().focus().toggleOrderedList().run();
  }

  if (editor.isActive('bulletList') || editor.isActive('taskList')) {
    return convertListType(editor, 'orderedList');
  }

  return continueOrderedList(editor);
}

export const OrderedListWithStart = OrderedList.extend({
  // Stock OrderedList binds Mod-Shift-7 to the raw toggleOrderedList command,
  // which bypasses this app's continueOrderedList/empty-paragraph logic.
  // KeyboardShortcuts (keyboard-shortcuts.ts) owns Mod-Shift-7/8/9 instead.
  addKeyboardShortcuts() {
    return {};
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      start: {
        default: 1,
        parseHTML: (element: HTMLElement) => toPositiveInteger(element.getAttribute('start'), 1),
        renderHTML: (attributes: Record<string, unknown>) => {
          const start = toPositiveInteger(attributes['start'], 1);
          if (start <= 1) {
            return {};
          }

          return { start: String(start) };
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['ol', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});
